import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
} from "@mui/material";
import { apiFetch } from "../../utils/api";
import { useAuth } from "../../contexts/AuthContext";
import { getAllowedScreenData } from "./screenScopes";

function formatNameWithId(name, id) {
  const cleanId = id != null && id !== "" ? String(id) : "Unassigned";
  return `${name} (ID: ${cleanId})`;
}

function SiteSetup() {
  const { auth, hasRole } = useAuth();

  // Edit permission:
  // - screens:edit can edit
  const canEditScreens = hasRole("screens:edit") // || hasRole("admin");

  const [tab, setTab] = useState(0);

  const [sites, setSites] = useState([]);
  const [locations, setLocations] = useState([]);
  const [sublocations, setSublocations] = useState([]);
  const [units, setUnits] = useState([]);

  // Dialog state
  const [open, setOpen] = useState(false);
  const [formMode, setFormMode] = useState("add"); // add | edit
  const [formType, setFormType] = useState("site"); // site | location | sublocation | unit
  const [formData, setFormData] = useState({});

  const handleChange = (_, newValue) => setTab(newValue);

  const ROLE_OPTIONS = [
    { value: "expo", label: "Expo" },
    { value: "prep", label: "Prep" },
    // { value: "mirror", label: "Mirror" },
    // { value: "runner", label: "Runner" },
  ];
  const TIMEZONE_OPTIONS = useMemo(() => {
    const fallback = [
      "America/New_York",
      "America/Chicago",
      "America/Denver",
      "America/Phoenix",
      "America/Los_Angeles",
      "America/Anchorage",
      "Pacific/Honolulu",
      "UTC",
    ];
    const isPreferredZone = (tz) => {
      const v = String(tz || "").trim();
      if (!v) return false;
      if (v === "UTC") return true;
      if (v.startsWith("Etc/")) return false;
      if (v.toUpperCase().startsWith("GMT")) return false;
      if (!v.includes("/")) return false;
      return true;
    };
    try {
      if (typeof Intl?.supportedValuesOf === "function") {
        const tz = Intl.supportedValuesOf("timeZone");
        const filtered = Array.isArray(tz) ? tz.filter(isPreferredZone) : [];
        if (filtered.length) {
          return [...new Set(["UTC", ...filtered])];
        }
        return fallback;
      }
    } catch {}
    return fallback;
  }, []);
  const DEFAULT_TIMEZONE = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  }, []);

  //  Load initial data
  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAll = async () => {
    await Promise.all([loadSites(), loadLocations(), loadSublocations(), loadUnits()]);
  };

  const loadSites = async () => {
    try {
      const res = await apiFetch("/updates-reports/sites");
      const data = await res.json();
      setSites(data);
    } catch (err) {
      console.error(" Failed to load sites", err);
      setSites([]);
    }
  };

  const loadLocations = async () => {
    try {
      const res = await apiFetch("/updates-reports/locations");
      const data = await res.json();
      setLocations(data);
    } catch (err) {
      console.error(" Failed to load locations", err);
      setLocations([]);
    }
  };

  const loadSublocations = async () => {
    try {
      const res = await apiFetch("/updates-reports/sublocations");
      const data = await res.json();
      setSublocations(data);
    } catch (err) {
      console.error(" Failed to load sublocations", err);
      setSublocations([]);
    }
  };

  const loadUnits = async () => {
    try {
      const res = await apiFetch("/updates-reports/stations");
      const data = await res.json();
      setUnits(data);
    } catch (err) {
      console.error(" Failed to load stations", err);
      setUnits([]);
    }
  };

  const { allowedSites, allowedLocations, allowedSublocations, allowedUnits } = useMemo(
    () =>
      getAllowedScreenData({
        roles: auth.roles || [],
        sites,
        locations,
        sublocations,
        units,
      }),
    [auth.roles, sites, locations, sublocations, units]
  );

  //  Open dialog for Add/Edit
  const openForm = (type, mode = "add", data = {}) => {
    if (!canEditScreens) return;

    setFormType(type);
    setFormMode(mode);

    if (type === "unit") {
      if (mode === "add") {
        const maxStationId = units.length
          ? Math.max(...units.map((u) => Number(u.station_id) || 0))
          : 0;

        setFormData({
          site_id: "",
          location_id: "",
          sublocation_id: "",
          station_id: maxStationId + 1,
          name: "",
          role: "expo",
        });
      } else {
        setFormData({
          ...data,
          name: data.station_name ?? data.name ?? "",
          role: data.role ?? "",
        });
      }
    } else {
      if (type === "location") {
        setFormData({
          ...data,
          timezone: data?.timezone || DEFAULT_TIMEZONE,
        });
      } else {
        setFormData(data);
      }
    }

    setOpen(true);
  };

  //  Submit handler
  const handleSubmit = async () => {
    if (!canEditScreens) return;

    let url = "";
    const method = formMode === "edit" ? "PUT" : "POST";

    switch (formType) {
      case "site":
        url = formMode === "edit" ? `/updates-reports/sites/${formData.id}` : "/updates-reports/sites";
        break;
      case "location":
        url = formMode === "edit" ? `/updates-reports/locations/${formData.id}` : "/updates-reports/locations";
        break;
      case "sublocation":
        url = formMode === "edit" ? `/updates-reports/sublocations/${formData.id}` : "/updates-reports/sublocations";
        break;
      case "unit":
        url = formMode === "edit" ? `/updates-reports/stations/${formData.id}` : "/updates-reports/stations";
        break;
      default:
        break;
    }

    try {
      await apiFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      setOpen(false);
      refreshAll();
    } catch (err) {
      console.error(` Failed to ${formMode} ${formType}`, err);
    }
  };

  //  Delete handler
  const handleDelete = async (type, id) => {
    if (!canEditScreens) return;

    let url = "";
    switch (type) {
      case "site":
        url = `/updates-reports/sites/${id}`;
        break;
      case "location":
        url = `/updates-reports/locations/${id}`;
        break;
      case "sublocation":
        url = `/updates-reports/sublocations/${id}`;
        break;
      case "unit":
        url = `/updates-reports/stations/${id}`;
        break;
      default:
        break;
    }

    try {
      await apiFetch(url, { method: "DELETE" });
      refreshAll();
    } catch (err) {
      console.error(` Failed to delete ${type}`, err);
    }
  };

  //  Render list as cards
  const renderCardList = (items, type, labelKey, subtitleFn, labelOverride) => (
    <Box sx={{ mt: 2, display: "grid", gap: 2 }}>
      {items.map((item) => (
        <Card
          key={item.id}
          sx={{
            borderRadius: "16px",
            boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
            transition: "all 0.2s ease",
            "&:hover": { transform: "scale(1.02)", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" },
          }}
        >
          <CardContent sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {labelOverride ? labelOverride(item) : formatNameWithId(item[labelKey], item.id)}
              </Typography>
              {subtitleFn && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {subtitleFn(item)}
                </Typography>
              )}
            </Box>

            <Box>
              <Button
                size="small"
                variant="outlined"
                sx={{ mr: 1, borderRadius: "12px" }}
                disabled={!canEditScreens}
                onClick={() => openForm(type, "edit", item)}
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                sx={{ borderRadius: "12px" }}
                disabled={!canEditScreens}
                onClick={() => handleDelete(type, item.id)}
              >
                Delete
              </Button>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const titleSuffix = canEditScreens ? "" : " (View Only)";

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, color: "#1f2937" }}>
        Site Setup{titleSuffix}
      </Typography>

      {!canEditScreens && (
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          You can view your allowed Sites/Locations/Sublocations/Units, but you do not have permission to edit them.
        </Typography>
      )}

      <Tabs
        value={tab}
        onChange={handleChange}
        sx={{
          "& .MuiTabs-flexContainer": { gap: 1 },
          "& .MuiTab-root": {
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            minHeight: 40,
            background: "#f3f4f6",
            "&.Mui-selected": { background: "#111827", color: "#fff" },
          },
        }}
      >
        <Tab label="Sites" />
        <Tab label="Locations" />
        <Tab label="Sublocations" />
        <Tab label="Units" />
      </Tabs>

      <Box sx={{ mt: 3 }}>
        {/* SITES */}
        {tab === 0 && (
          <>
            <Button variant="contained" sx={{ borderRadius: "12px", mb: 2 }} disabled={!canEditScreens} onClick={() => openForm("site")}>
              + Add Site
            </Button>
            {renderCardList(allowedSites, "site", "name")}
          </>
        )}

        {/* LOCATIONS */}
        {tab === 1 && (
          <>
            <Button variant="contained" sx={{ borderRadius: "12px", mb: 2 }} disabled={!canEditScreens} onClick={() => openForm("location")}>
              + Add Location
            </Button>

            {renderCardList(
              [...allowedLocations].sort((a, b) => Number(a.id) - Number(b.id)),
              "location",
              "name",
              (l) => `Site ID: ${l.site_id} • Timezone: ${l.timezone || "UTC"}`
            )}
          </>
        )}

        {/* SUBLOCATIONS: Site -> Location -> Sublocations (visual hierarchy) */}
        {tab === 2 && (
          <>
            <Button variant="contained" sx={{ borderRadius: "12px", mb: 2 }} disabled={!canEditScreens} onClick={() => openForm("sublocation")}>
              + Add Sublocation
            </Button>

            {[...allowedSites]
              .sort((a, b) => Number(a.id) - Number(b.id))
              .map((site) => {
                const locsForSite = allowedLocations
                  .filter((l) => l.site_id === site.id)
                  .slice()
                  .sort((a, b) => Number(a.id) - Number(b.id));

                if (!locsForSite.length) return null;

                return (
                  <Box key={site.id} sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: "#111827" }}>
                      Site: {formatNameWithId(site.name, site.id)}
                    </Typography>

                    {locsForSite.map((loc) => {
                      const subsForLoc = allowedSublocations
                        .filter((sl) => sl.location_id === loc.id)
                        .slice()
                        .sort((a, b) => Number(a.id) - Number(b.id));

                      if (!subsForLoc.length) return null;

                      return (
                        <Box key={loc.id} sx={{ ml: 2, mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: "#374151" }}>
                            Location: {formatNameWithId(loc.name, loc.id)}
                          </Typography>

                          {renderCardList(
                            subsForLoc,
                            "sublocation",
                            "name",
                            (sl) => `Sublocation ID: ${sl.id}`
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
          </>
        )}

        {/* UNITS: Site -> Location -> Sublocation -> Units (visual hierarchy) */}
        {tab === 3 && (
          <>
            <Button variant="contained" sx={{ borderRadius: "12px", mb: 2 }} disabled={!canEditScreens} onClick={() => openForm("unit")}>
              + Add Unit
            </Button>

            {[...allowedSites]
              .sort((a, b) => Number(a.id) - Number(b.id))
              .map((site) => {
                const locsForSite = allowedLocations
                  .filter((l) => l.site_id === site.id)
                  .slice()
                  .sort((a, b) => Number(a.id) - Number(b.id));

                // Only show site if it has any units underneath it
                const hasAnyUnitsForSite = allowedUnits.some((u) => u.site_id === site.id);
                if (!hasAnyUnitsForSite) return null;

                return (
                  <Box key={site.id} sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: "#111827" }}>
                      Site: {formatNameWithId(site.name, site.id)}
                    </Typography>

                    {locsForSite.map((loc) => {
                      const hasUnitsForLocation = allowedUnits.some((u) => u.location_id === loc.id);
                      if (!hasUnitsForLocation) return null;

                      const subsForLoc = allowedSublocations
                        .filter((sl) => sl.location_id === loc.id)
                        .slice()
                        .sort((a, b) => Number(a.id) - Number(b.id));

                      return (
                        <Box key={loc.id} sx={{ ml: 2, mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: "#374151" }}>
                            Location: {formatNameWithId(loc.name, loc.id)}
                          </Typography>

                          {subsForLoc.map((sl) => {
                            const unitsForSub = allowedUnits
                              .filter((u) => u.sublocation_id === sl.id)
                              .slice()
                              .sort((a, b) => Number(a.station_id || 0) - Number(b.station_id || 0));

                            if (!unitsForSub.length) return null;

                            return (
                              <Box key={sl.id} sx={{ ml: 2, mb: 2 }}>
                                <Typography variant="body1" sx={{ fontWeight: 700, mb: 1, color: "#4b5563" }}>
                                  Sublocation: {formatNameWithId(sl.name, sl.id)}
                                </Typography>

                                {renderCardList(
                                  unitsForSub,
                                  "unit",
                                  "station_name",
                                  (u) => `Station ID: ${u.station_id} • Role: ${u.role}`,
                                  (u) => u.station_name || u.name || "Unit"
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
          </>
        )}
      </Box>

      {/* Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "20px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {formMode === "add" ? `Add ${formType}` : `Edit ${formType}`}
        </DialogTitle>

        <DialogContent>
          {!canEditScreens ? (
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
              You do not have permission to edit screens.
            </Typography>
          ) : (
            <>
              {formType === "site" && (
                <TextField
                  fullWidth
                  margin="normal"
                  label="Site Name"
                  value={formData.name || ""}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              )}

              {formType === "location" && (
                <>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Site</InputLabel>
                    <Select
                      value={formData.site_id || ""}
                      onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
                    >
                      {allowedSites.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {formatNameWithId(s.name, s.id)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    margin="normal"
                    label="Location Name"
                    value={formData.name || ""}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />

                  <FormControl fullWidth margin="normal">
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={formData.timezone || DEFAULT_TIMEZONE}
                      label="Timezone"
                      onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    >
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <MenuItem key={tz} value={tz}>
                          {tz}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}

              {formType === "sublocation" && (
                <>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={formData.location_id || ""}
                      onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                    >
                      {allowedLocations.map((l) => (
                        <MenuItem key={l.id} value={l.id}>
                          {formatNameWithId(l.name, l.id)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    margin="normal"
                    label="Sublocation Name"
                    value={formData.name || ""}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </>
              )}

              {formType === "unit" && (
                <>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Site</InputLabel>
                    <Select
                      value={formData.site_id || ""}
                      onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
                    >
                      {allowedSites.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {formatNameWithId(s.name, s.id)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth margin="normal">
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={formData.location_id || ""}
                      onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                    >
                      {allowedLocations
                        .filter((l) => String(l.site_id) === String(formData.site_id))
                        .map((l) => (
                          <MenuItem key={l.id} value={l.id}>
                            {formatNameWithId(l.name, l.id)}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth margin="normal">
                    <InputLabel>Sublocation</InputLabel>
                    <Select
                      value={formData.sublocation_id || ""}
                      onChange={(e) => setFormData({ ...formData, sublocation_id: e.target.value })}
                    >
                      {allowedSublocations
                        .filter((sl) => String(sl.location_id) === String(formData.location_id))
                        .map((sl) => (
                          <MenuItem key={sl.id} value={sl.id}>
                            {formatNameWithId(sl.name, sl.id)}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    margin="normal"
                    label="Station ID"
                    value={formData.station_id || ""}
                    InputProps={{ readOnly: true }}
                  />

                  <TextField
                    fullWidth
                    margin="normal"
                    label="Unit Name"
                    value={formData.name || ""}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />

                  <FormControl fullWidth margin="normal">
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={formData.role || ""}
                      label="Role"
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <MenuItem key={r.value} value={r.value}>
                          {r.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)} sx={{ borderRadius: "12px" }}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ borderRadius: "12px" }} disabled={!canEditScreens}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SiteSetup;
